import { Component, ChangeDetectionStrategy, input, output, model } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Select } from '@openng/optimus-ui/select';

import { PropertyFilterOption } from 'src/app/models/property-filter-option.model';

@Component({
	selector: 'app-property-filter-panel',
	imports: [FormsModule, Select],
	templateUrl: './property-filter-panel.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './property-filter-panel.component.scss',
})
export class PropertyFilterPanelComponent {
	readonly title = input.required<string>();

	readonly keys = input.required<string[]>();

	readonly filters = input.required<Record<string, string | null>>();

	readonly optionsFn = input.required<(key: string) => PropertyFilterOption[]>();

	readonly level = input<string>('');

	readonly filterChange = output<{
		key: string;
		value: string | null;
	}>();

	readonly expanded = model<boolean>(false);

	public get visibleKeys(): string[] {
		return this.keys().filter(key => this.optionsFn()(key).length > 0);
	}

	public getFilterValue(key: string): string | null {
		const level = this.level();
		const namespacedKey = level ? `${level}:${key}` : key;
		return this.filters()[namespacedKey] ?? null;
	}

	public onFilterChange(key: string, value: string | null): void {
		const level = this.level();
		const namespacedKey = level ? `${level}:${key}` : key;
		this.filterChange.emit({ key: namespacedKey, value });
	}

	/**
	 * Known acronyms that must render fully upper-cased rather than Title-Cased.
	 * Matched case-insensitively per word.
	 * Add new acronyms here rather than special-casing individual keys.
	 */
	private static readonly ACRONYMS = new Set(['dtc', 'ecu', 'sop']);

	/**
	 * Turns a raw property key into a display label: underscores become spaces, each word is Title-Cased,
	 * and recognised acronyms (ACRONYMS) are fully upper-cased.
	 * @param key
	 */
	public formatKey(key: string): string {
		return key
			.split('_')
			.map(word => (PropertyFilterPanelComponent.ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.replace(/\b\w/g, c => c.toUpperCase())))
			.join(' ');
	}
}
